import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, History, Shield, Calendar } from "lucide-react";
import { useTalkStore, TalkDebate } from "@/data/talkStore";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function TalkArchivesPage() {
  const { lang, isRtl } = useI18n();
  const [archivedDebates, setArchivedDebates] = useState<TalkDebate[]>([]);

  const { debates, fetchDebates } = useTalkStore();

  useEffect(() => {
    fetchDebates();
  }, [fetchDebates]);

  useEffect(() => {
    const closed = debates
      .filter(d => d.status === "cloture")
      .sort((a,b) => {
        const dateA = new Date(a.createdAt.replace(' ', 'T')).getTime();
        const dateB = new Date(b.createdAt.replace(' ', 'T')).getTime();
        return dateB - dateA;
      });
    setArchivedDebates(closed);
  }, [debates]);

  const groupedDebates = useMemo(() => {
    const groups: { [monthYear: string]: TalkDebate[] } = {};
    archivedDebates.forEach(d => {
       const closedStr = d.closedAt ? d.closedAt.replace(' ', 'T') : d.createdAt.replace(' ', 'T');
       const dateToCheck = new Date(closedStr);
       const monthStr = dateToCheck.toLocaleDateString(lang === 'ar' ? 'ar-MA' : 'fr-FR', { month: 'long', year: 'numeric' });
       // Capitalize first letter in french
       const key = monthStr.charAt(0).toUpperCase() + monthStr.slice(1);
       if(!groups[key]) groups[key] = [];
       groups[key].push(d);
    });
    return groups;
  }, [archivedDebates, lang]);

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={isRtl ? 'rtl' : 'ltr'}>
      <Navbar />

      <main className="flex-1 pt-24 pb-16 relative">
         <div className="absolute top-0 inset-x-0 h-48 bg-gradient-to-b from-indigo-50 to-background dark:from-indigo-950/20 -z-10"></div>
         
         <div className="container px-4 md:px-6">
            <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-indigo-600 dark:text-indigo-400" asChild>
               <Link to="/talk">
                 <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />
                 {lang === 'ar' ? "العودة إلى نقاش كاينين" : "Retour à Kayn'in Talk"}
               </Link>
            </Button>

            <div className="mb-10 text-center">
               <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-body text-sm font-extrabold mb-1.5 border border-indigo-200 dark:border-indigo-800">
                  <History className="w-4 h-4" /> 
                  {lang === 'ar' ? "الأرشيف" : "Archives"}
               </span>
               <h1 className="text-3xl md:text-5xl font-display font-extrabold text-foreground mb-4">
                  {lang === 'ar' ? "الأسئلة السابقة" : "Questions des mois passés"}
               </h1>
               <p className="text-muted-foreground max-w-2xl mx-auto">
                  {lang === 'ar' 
                     ? "تصفح السجل الكامل لجميع المناقشات المغلقة واستكشف الخلاصات الإدارية." 
                     : "Parcourez l'historique complet de tous les débats clôturés et explorez les conclusions de chaque mois."}
               </p>
            </div>

            {Object.keys(groupedDebates).length === 0 ? (
               <div className="text-center py-20 border border-dashed rounded-3xl text-muted-foreground">
                  {lang === 'ar' ? "لا يوجد أي نقاش مؤرشف حتى الآن." : "Aucun débat archivé pour le moment."}
               </div>
            ) : (
               <div className="space-y-12">
                  {Object.entries(groupedDebates).map(([monthYear, debates], groupIndex) => (
                     <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
                        key={monthYear} 
                        className="bg-card border border-border shadow-sm rounded-3xl p-6 md:p-8"
                     >
                        <h2 className="text-2xl font-display font-bold flex items-center gap-3 mb-6 pb-4 border-b border-border">
                           <Calendar className="w-6 h-6 text-indigo-500" />
                           <span className="capitalize">{monthYear}</span>
                        </h2>
                        
                        <div className="flex flex-col gap-4">
                           {debates.map((pd, idx) => (
                              <motion.div 
                                 initial={{ opacity: 0, scale: 0.95 }}
                                 whileInView={{ opacity: 1, scale: 1 }}
                                 viewport={{ once: true }}
                                 transition={{ delay: 0.1 + idx * 0.05 }}
                                 key={pd.id} 
                                 className="p-5 bg-muted/20 hover:bg-muted/60 border border-border rounded-2xl flex flex-col md:flex-row justify-between md:items-center gap-6 transition-all group hover:shadow-md"
                              >
                                 <div className="flex-1">
                                    <h4 className="font-bold text-lg text-foreground mb-2 group-hover:text-indigo-600 transition-colors">{pd.title}</h4>
                                    <p className="text-sm text-muted-foreground mb-4 whitespace-pre-wrap">{pd.description}</p>
                                    {pd.adminConclusion && (
                                       <div className="bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-4 text-sm flex gap-3 shadow-sm">
                                          <Shield className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                                          <p className="text-foreground/90 italic leading-relaxed">
                                             <span className="font-semibold text-indigo-600 dark:text-indigo-400 mr-2">{lang === 'ar' ? 'خلاصة:' : 'Conclusion :'}</span>
                                             {pd.adminConclusion}
                                          </p>
                                       </div>
                                    )}
                                 </div>
                                 <div className="flex items-center gap-2 shrink-0 text-sm font-semibold text-indigo-600 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 rounded-xl whitespace-nowrap">
                                    {lang === 'ar' ? "مغلق" : "Clôturé"}
                                 </div>
                              </motion.div>
                           ))}
                        </div>
                     </motion.div>
                  ))}
               </div>
            )}
         </div>
      </main>

      <Footer />
    </div>
  );
}
